// This file is mock-implemented for offline Pure React execution.
import type { Database } from "./types";

// Content tables intentionally start empty. Data must come from an API or be
// created by the user; the application no longer seeds demo JSON records.

// Storage for Object URLs of uploaded files
const storageUrlsKey = "mock_storage_urls";
function getStorageUrlMap(): Record<string, string> {
  const data = localStorage.getItem(storageUrlsKey);
  return data ? JSON.parse(data) : {};
}
function setStorageUrl(pathStr: string, url: string) {
  const map = getStorageUrlMap();
  map[pathStr] = url;
  localStorage.setItem(storageUrlsKey, JSON.stringify(map));
}

// Get or set localStorage tables
function getLocalStorageTable(table: string): any[] {
  // New namespace prevents previously cached seed records from resurfacing.
  const key = `app_db_${table}`;
  const data = localStorage.getItem(key);
  if (data) {
    try {
      return JSON.parse(data);
    } catch (e) {
      console.error(`Failed to parse table data for ${table}`, e);
    }
  }
  localStorage.setItem(key, "[]");
  return [];
}

function setLocalStorageTable(table: string, data: any[]): void {
  const key = `app_db_${table}`;
  localStorage.setItem(key, JSON.stringify(data));
}

// Authentication listeners list
const authCallbacks: Array<(event: string, session: any) => void> = [];

function triggerAuthChange(event: string, session: any) {
  authCallbacks.forEach(cb => {
    try {
      cb(event, session);
    } catch (e) {
      console.error("Auth state change callback error:", e);
    }
  });
}

class MockBuilder {
  table: string;
  data: any[];
  _updatePayload: any = null;
  _isDelete = false;

  constructor(table: string) {
    this.table = table;
    this.data = getLocalStorageTable(table);
  }

  select(fields = "*") {
    // Handle specific relations
    if (this.table === "tickets" && fields.includes("ticket_categories")) {
      const categories = getLocalStorageTable("ticket_categories");
      this.data = this.data.map(ticket => ({
        ...ticket,
        category: categories.find(c => c.id === ticket.category_id) || null,
      }));
    }
    return this;
  }

  eq(field: string, value: any) {
    this.data = this.data.filter(item => item && item[field] == value);
    return this;
  }

  neq(field: string, value: any) {
    this.data = this.data.filter(item => item && item[field] != value);
    return this;
  }

  gte(field: string, value: any) {
    this.data = this.data.filter(item => item && item[field] >= value);
    return this;
  }

  lte(field: string, value: any) {
    this.data = this.data.filter(item => item && item[field] <= value);
    return this;
  }

  gt(field: string, value: any) {
    this.data = this.data.filter(item => item && item[field] > value);
    return this;
  }

  lt(field: string, value: any) {
    this.data = this.data.filter(item => item && item[field] < value);
    return this;
  }

  like(field: string, pattern: string) {
    const cleanPattern = pattern.replace(/%/g, ".*");
    const regex = new RegExp(cleanPattern, "i");
    this.data = this.data.filter(item => item && regex.test(item[field] || ""));
    return this;
  }

  ilike(field: string, pattern: string) {
    return this.like(field, pattern);
  }

  in(field: string, values: any[]) {
    this.data = this.data.filter(item => item && values.includes(item[field]));
    return this;
  }

  or(filterString: string) {
    const filters = filterString.split(",");
    this.data = this.data.filter(item => {
      return filters.some(filter => {
        const parts = filter.split(".");
        if (parts.length < 3) {
          return false;
        }
        const field = parts[0];
        const operator = parts[1];
        let val = parts[2];
        if (val.startsWith("%") && val.endsWith("%")) {
          val = val.substring(1, val.length - 1);
        }
        if (operator === "ilike" || operator === "like") {
          return String(item[field] || "")
            .toLowerCase()
            .includes(val.toLowerCase());
        }
        if (operator === "eq") {
          return item[field] == val;
        }
        return false;
      });
    });
    return this;
  }

  order(field: string, { ascending = true } = {}) {
    this.data = [...this.data].sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      if (valA === valB) {
        return 0;
      }
      if (valA === null || valA === undefined) {
        return 1;
      }
      if (valB === null || valB === undefined) {
        return -1;
      }
      return (valA < valB ? -1 : 1) * (ascending ? 1 : -1);
    });
    return this;
  }

  limit(num: number) {
    this.data = this.data.slice(0, num);
    return this;
  }

  range(from: number, to: number) {
    this.data = this.data.slice(from, to + 1);
    return this;
  }

  insert(recordOrRecords: any) {
    const list = Array.isArray(recordOrRecords)
      ? recordOrRecords
      : [recordOrRecords];
    const currentData = getLocalStorageTable(this.table);
    const newRecords = list.map(record => ({
      id: record.id || Math.random().toString(36).substring(7),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      ...record,
    }));

    const updatedData = [...currentData, ...newRecords];
    setLocalStorageTable(this.table, updatedData);
    this.data = newRecords;

    return this;
  }

  update(updateData: any) {
    this._updatePayload = updateData;
    return this;
  }

  delete() {
    this._isDelete = true;
    return this;
  }

  maybeSingle() {
    return {
      then: (onfulfilled: any) => {
        const item = this.data.length > 0 ? this.data[0] : null;
        return Promise.resolve(onfulfilled({ data: item, error: null }));
      },
    };
  }

  single() {
    return {
      then: (onfulfilled: any) => {
        const item = this.data.length > 0 ? this.data[0] : null;
        if (!item) {
          return Promise.resolve(
            onfulfilled({ data: null, error: new Error("No rows found") })
          );
        }
        return Promise.resolve(onfulfilled({ data: item, error: null }));
      },
    };
  }

  then(onfulfilled: any, onrejected?: any) {
    const allItems = getLocalStorageTable(this.table);

    if (this._updatePayload) {
      const targetIds = this.data.map(item => item.id);
      const updatedList: any[] = [];
      const newAllItems = allItems.map(item => {
        if (targetIds.includes(item.id)) {
          const updatedItem = {
            ...item,
            ...this._updatePayload,
            updated_at: new Date().toISOString(),
          };
          updatedList.push(updatedItem);
          return updatedItem;
        }
        return item;
      });
      setLocalStorageTable(this.table, newAllItems);
      this.data = updatedList;
      return Promise.resolve({
        data: Array.isArray(this.data) ? this.data : this.data[0],
        error: null,
      }).then(onfulfilled, onrejected);
    }

    if (this._isDelete) {
      const targetIds = this.data.map(item => item.id);
      const newAllItems = allItems.filter(item => !targetIds.includes(item.id));
      setLocalStorageTable(this.table, newAllItems);
      return Promise.resolve({ data: this.data, error: null }).then(
        onfulfilled,
        onrejected
      );
    }

    return Promise.resolve({ data: this.data, error: null }).then(
      onfulfilled,
      onrejected
    );
  }
}

// Define the mock supabase instance
export const supabase = {
  from: (table: string) => {
    return new MockBuilder(table);
  },

  auth: {
    getUser: async () => {
      const sessionStr = localStorage.getItem("mock_session");
      if (sessionStr) {
        const session = JSON.parse(sessionStr);
        return { data: { user: session.user }, error: null };
      }
      return { data: { user: null }, error: null };
    },

    getSession: async () => {
      const sessionStr = localStorage.getItem("mock_session");
      if (sessionStr) {
        const session = JSON.parse(sessionStr);
        return { data: { session }, error: null };
      }
      return { data: { session: null }, error: null };
    },

    signInWithPassword: async ({ email, password }: any) => {
      // Determine role based on email pattern
      let role = "user";
      if (email.includes("admin")) {
        role = email.includes("super") ? "superadmin" : "admin";
      } else if (email.includes("librarian") || email.includes("lib")) {
        role = "librarian";
      } else if (email.includes("scholar")) {
        role = "scholar";
      } else if (email.includes("lms") || email.includes("student")) {
        role = "lms";
      }

      // Check if user exists in profiles, otherwise create
      const profiles = getLocalStorageTable("profiles");
      const userRoles = getLocalStorageTable("user_roles");

      const existingProfile = profiles.find(p => p.email === email);
      const userId =
        existingProfile?.user_id || Math.random().toString(36).substring(7);

      if (!existingProfile) {
        const newProfile = {
          id: userId,
          user_id: userId,
          email,
          full_name: email.split("@")[0].replace(/\b\w/g, c => c.toUpperCase()),
          avatar_url: null,
          created_at: new Date().toISOString(),
        };
        profiles.push(newProfile);
        setLocalStorageTable("profiles", profiles);

        userRoles.push({
          user_id: userId,
          role,
          created_at: new Date().toISOString(),
        });
        setLocalStorageTable("user_roles", userRoles);
      } else {
        const existingRole = userRoles.find(r => r.user_id === userId);
        if (existingRole) {
          role = existingRole.role;
        } else {
          userRoles.push({
            user_id: userId,
            role,
            created_at: new Date().toISOString(),
          });
          setLocalStorageTable("user_roles", userRoles);
        }
      }

      const mockUser = {
        id: userId,
        email,
        user_metadata: {
          full_name: email.split("@")[0],
        },
      };

      const mockSession = {
        access_token: "mock-token",
        token_type: "bearer",
        expires_in: 3600,
        refresh_token: "mock-refresh",
        user: mockUser,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
      };

      localStorage.setItem("mock_session", JSON.stringify(mockSession));
      triggerAuthChange("SIGNED_IN", mockSession);

      return { data: { user: mockUser, session: mockSession }, error: null };
    },

    signUp: async ({ email, password, options }: any) => {
      const profiles = getLocalStorageTable("profiles");
      const userRoles = getLocalStorageTable("user_roles");

      const existingProfile = profiles.find(p => p.email === email);
      if (existingProfile) {
        return {
          data: { user: null },
          error: new Error("User already exists"),
        };
      }

      const userId = Math.random().toString(36).substring(7);
      const fullName = options?.data?.full_name || email.split("@")[0];

      const newProfile = {
        id: userId,
        user_id: userId,
        email,
        full_name: fullName,
        avatar_url: null,
        created_at: new Date().toISOString(),
      };
      profiles.push(newProfile);
      setLocalStorageTable("profiles", profiles);

      userRoles.push({
        user_id: userId,
        role: "user",
        created_at: new Date().toISOString(),
      });
      setLocalStorageTable("user_roles", userRoles);

      const mockUser = {
        id: userId,
        email,
        user_metadata: {
          full_name: fullName,
        },
      };

      const mockSession = {
        access_token: "mock-token",
        token_type: "bearer",
        expires_in: 3600,
        refresh_token: "mock-refresh",
        user: mockUser,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
      };

      localStorage.setItem("mock_session", JSON.stringify(mockSession));
      triggerAuthChange("SIGNED_IN", mockSession);

      return { data: { user: mockUser, session: mockSession }, error: null };
    },

    signOut: async () => {
      localStorage.removeItem("mock_session");
      triggerAuthChange("SIGNED_OUT", null);
      return { error: null };
    },

    onAuthStateChange: (callback: any) => {
      authCallbacks.push(callback);

      // Instantly trigger initial check if session exists
      const sessionStr = localStorage.getItem("mock_session");
      if (sessionStr) {
        const session = JSON.parse(sessionStr);
        setTimeout(() => callback("SIGNED_IN", session), 0);
      } else {
        setTimeout(() => callback("SIGNED_OUT", null), 0);
      }

      return {
        data: {
          subscription: {
            unsubscribe: () => {
              const index = authCallbacks.indexOf(callback);
              if (index > -1) {
                authCallbacks.splice(index, 1);
              }
            },
          },
        },
      };
    },
  },

  storage: {
    from: (bucket: string) => {
      return {
        upload: async (pathStr: string, file: File, options?: any) => {
          // Generate a local object URL to make it interactive in-browser
          const objectUrl = URL.createObjectURL(file);
          setStorageUrl(pathStr, objectUrl);
          return { data: { path: pathStr }, error: null };
        },
        getPublicUrl: (pathStr: string) => {
          const map = getStorageUrlMap();
          const publicUrl =
            map[pathStr] ||
            `https://placehold.co/600x400?text=${encodeURIComponent(pathStr)}`;
          return { data: { publicUrl } };
        },
      };
    },
  },
};
